import React from "react";
import { ArrowLeft, Sparkles, ChevronDown, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function CallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const benefits = [{
    id: 1,
    text: "זמינות מיידית תוך 5 שעות בלבד בכל הארץ"
  }, {
    id: 2,
    text: "מטפלים ישראלים מוסמכים ודוברי עברית שפת אם"
  }, {
    id: 3,
    text: "שירות מותאם אישית בבית המטופל או בבתי החולים"
  }, {
    id: 4,
    text: "ליווי מקצועי ואישי לכל אורך הדרך 24/7"
  }];
  const faqs = [{
    id: 1,
    question: "תוך כמה זמן מגיע מטפל?",
    answer: "אנחנו מתחייבים להגעת מטפל תוך 5 שעות מרגע הפנייה, בפריסה ארצית מלאה."
  }, {
    id: 2,
    question: "האם השירות ניתן גם בבתי חולים?",
    answer: "כן, אנו מספקים שירותי השגחה וסיעוד פרטי גם בבית וגם בכל בתי החולים בארץ."
  }, {
    id: 3,
    question: "מי הם המטפלים שלכם?",
    answer: "כל המטפלים שלנו הם ישראלים מוסמכים, דוברי עברית, שעברו תהליך סינון קפדני וראיונות אישיים."
  }];
  return <section data-section-id="33539" id="חסרת-זמן-קבלו-מטפל-סיעודי-עכשיו" className="relative w-full py-24 overflow-hidden bg-background text-foreground">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] max-w-[1400px] opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-chart-3)_0%,transparent_70%)] blur-[120px]" />
      </div>

      <div ref={ref} className={cn("relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px] transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="relative overflow-hidden border border-border/50 bg-white/80 backdrop-blur-md shadow-xl">
          
          <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(255,255,255,0.4)_0%,rgba(255,255,255,0)_30%,rgba(255,255,255,0.2)_100%)] pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-chart-3/10 blur-[60px] rounded-lg pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-chart-2/10 blur-[60px] rounded-lg pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center justify-center text-center py-16 px-6 md:py-24 md:px-12 gap-12">
            
            <div className="space-y-12 max-w-4xl mx-auto w-full text-right">
              <div className="space-y-4">
                <h3 className="text-xl md:text-2xl font-bold text-primary/80">מבוא לשירותי הסיעוד</h3>
                <p className="text-lg text-slate-600 font-sans leading-relaxed">
                  הצורך במענה סיעודי מקצועי ומיידי הוא אחד האתגרים המשמעותיים ביותר שעמם מתמודדות משפחות בישראל כיום. אנחנו מבינים את הדחיפות ואת הרצון להעניק ליקיריכם את הטיפול הטוב ביותר ללא פשרות.
                </p>
              </div>

              <div className="space-y-6 text-center">
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-default font-bold text-primary tracking-tight">
                  צריכים מטפל סיעודי עכשיו?
                </h2>
                
                <p className="text-lg md:text-xl text-slate-600 font-sans font-medium leading-relaxed max-w-2xl mx-auto">
                  הצטרפו למאות משפחות שכבר מצאו שקט נפשי. אנחנו כאן כדי לספק לכם מטפל סיעודי מקצועי, אמין ומסור תוך שעות ספורות, עם ליווי אישי לכל אורך הדרך.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-primary">הפתרון המהיר ביותר בישראל</h3>
                <p className="text-lg text-slate-600 font-sans leading-relaxed">
                  אנו ב-Siudy פיתחנו מערך תגובה מהיר המאפשר לנו לשדך בין מטופלים למטפלים ישראלים מוסמכים תוך חמש שעות בלבד. הפתרון שלנו כולל סינון קפדני, התאמה אישיותית וזמינות מלאה לכל צורך שעולה.
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-primary">למה לבחור בסיעוד מיידי?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-center gap-3 bg-white/40 p-4 rounded-2xl border border-border/30">
                      <CircleCheck className="w-6 h-6 text-chart-2 shrink-0" />
                      <span className="text-lg text-slate-700 font-medium">{benefit.text}</span>
                    </div>)}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-primary">תהליך מקצועי ומבוקר</h3>
                <p className="text-lg text-slate-600 font-sans leading-relaxed">
                  כל תהליך הגיוס וההצבה שלנו מלווה בפיקוח הדוק. אנו לא רק שולחים מטפל, אלא מוודאים שהוא עובר הכשרה מתאימה למקרה הספציפי, דואגים לכל הכיסוי הביטוחי הנדרש ומבצעים בקרה שוטפת לאורך כל תקופת השירות.
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-primary">שאלות ותשובות</h3>
                <div className="space-y-4">
                  {faqs.map((faq, i) => <details key={faq.id} data-index={i} className="group border border-border/50 rounded-2xl bg-white/50 overflow-hidden transition-all duration-300">
                      <summary className="flex items-center justify-between p-5 cursor-pointer list-none font-bold text-slate-700 hover:bg-accent/30 transition-colors">
                        <span>{faq.question}</span>
                        <ChevronDown className="w-5 h-5 transition-transform group-open:rotate-180 text-chart-3" />
                      </summary>
                      <div className="p-5 pt-0 text-slate-600 leading-relaxed border-t border-border/20 bg-white/30">
                        {faq.answer}
                      </div>
                    </details>)}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-8 pt-4">
              <Button asChild size="lg" className="relative group overflow-hidden bg-chart-3 hover:bg-chart-3/90 text-white text-lg px-10 py-7 rounded-lg hover:shadow-[0_0_40px_rgba(20,184,166,0.6)] transition-all duration-300 hover:scale-105 border border-white/20">
                <Link to="/ContactPage">
                  <span className="relative z-10 flex items-center gap-2 font-default font-bold">
                    התחילו עכשיו - ייעוץ ראשוני חינם
                    <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out skew-x-12" />
                  <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(circle_at_center,white_0%,transparent_70%)] mix-blend-overlay" />
                </Link>
              </Button>

              <Button asChild size="lg" className="relative group overflow-hidden bg-chart-3 hover:bg-chart-3/90 text-white text-lg px-10 py-7 rounded-lg hover:shadow-[0_0_40px_rgba(20,184,166,0.6)] transition-all duration-300 hover:scale-105 border border-white/20">
                <Link to="/ContactPage">
                  <span className="relative z-10 flex items-center gap-2 font-default font-bold">
                    התקשרו עכשיו לייעוץ
                    <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out skew-x-12" />
                  <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(circle_at_center,white_0%,transparent_70%)] mix-blend-overlay" />
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-4 text-slate-500 text-sm font-sans font-medium">
              <div className="flex items-center gap-2 bg-accent/50 px-4 py-2 rounded-lg backdrop-blur-sm border border-border/50">
                <Sparkles className="w-4 h-4 text-chart-2" />
                <span>מטפלים מוסמכים בלבד</span>
              </div>
              <div className="flex items-center gap-2 bg-accent/50 px-4 py-2 rounded-lg backdrop-blur-sm border border-border/50">
                <Sparkles className="w-4 h-4 text-chart-2" />
                <span>זמינות מיידית 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}